import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ModalShell } from "./ModalShell";
import { classifyScan } from "../messaging/verifyScan";

type ScanOutcome =
  | { kind: "scanning" }
  | { kind: "starting" }
  | { kind: "match" }
  | { kind: "mismatch" }
  | { kind: "notverify" }
  | { kind: "error"; message: string };

/**
 * FR-CON-002 camera flow: scan the contact's on-screen verify QR and compare
 * the decoded digits with the locally computed safety number. The digits match
 * only if both devices hold the same identity keys — a match verifies, a
 * mismatch is surfaced loudly and never marks the contact verified.
 */
export function QrScannerModal({
  displayName,
  expectedDigits,
  onVerified,
  onClose,
}: {
  displayName: string;
  expectedDigits: string;
  onVerified: () => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [outcome, setOutcome] = useState<ScanOutcome>({ kind: "starting" });
  const [round, setRound] = useState(0);
  // classifyScan normalizes internally — keep the raw value here.
  const expectedRef = useRef(expectedDigits);
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;

  useEffect(() => {
    let raf = 0;
    let stream: MediaStream | null = null;
    let cancelled = false;
    let decided = false;

    function stopCamera() {
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
    }

    (async () => {
      if (!window.isSecureContext) {
        setOutcome({
          kind: "error",
          message: "Camera needs a secure (https) connection. Compare the numbers below instead.",
        });
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setOutcome({
          kind: "error",
          message: "This browser has no camera API. Compare the numbers below instead.",
        });
        return;
      }
      try {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "environment" },
            audio: false,
          });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        }
      } catch {
        setOutcome({
          kind: "error",
          message: "Camera unavailable or permission denied. Allow camera access, or compare the numbers below instead.",
        });
        return;
      }
      if (cancelled) {
        stopCamera();
        return;
      }
      const video = videoRef.current;
      if (!video) {
        stopCamera();
        return;
      }
      video.srcObject = stream;
      // React skips the muted attribute; set the property for autoplay policy.
      video.muted = true;
      video.setAttribute("playsinline", "");
      await video.play().catch(() => {});
      const jsQR = (await import("jsqr")).default;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      setOutcome({ kind: "scanning" });

      const tick = () => {
        if (cancelled || decided) return;
        if (!ctx || !video.videoWidth) {
          raf = requestAnimationFrame(tick);
          return;
        }
        if (canvas.width !== video.videoWidth) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        }
        ctx.drawImage(video, 0, 0);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
        if (!code?.data) {
          raf = requestAnimationFrame(tick);
          return;
        }
        decided = true;
        stopCamera();
        const classification = classifyScan(code.data, expectedRef.current);
        if (classification === "match") {
          setOutcome({ kind: "match" });
          onVerifiedRef.current();
        } else if (classification === "notverify") {
          setOutcome({ kind: "notverify" });
        } else {
          setOutcome({ kind: "mismatch" });
        }
      };
      raf = requestAnimationFrame(tick);
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [round]);

  const busy = outcome.kind === "starting" || outcome.kind === "scanning";

  const modal = (
    <ModalShell
      title="Scan their code"
      ariaLabel="Scan verify QR code"
      onClose={onClose}
      size="sm"
      className="scanner-modal"
    >
      <div className="stack">
        <p className="muted" style={{ margin: 0 }}>
          Ask {displayName} to open this chat → <strong>Details → Show my code</strong>, then point
          your camera at their screen.
        </p>

        {(busy || outcome.kind === "match") && (
          <div className="scanner-frame" aria-hidden={outcome.kind === "match"}>
            <video ref={videoRef} muted autoPlay playsInline />
            {busy && <div className="scanner-reticle" />}
          </div>
        )}

        {busy && (
          <p className="scan-status muted" role="status" style={{ margin: 0 }}>
            {outcome.kind === "starting" ? "Starting camera…" : "Looking for the code…"}
          </p>
        )}

        {outcome.kind === "match" && (
          <>
            <p className="scan-status scan-ok" role="status" style={{ margin: 0 }}>
              ✓ Match — {displayName} is now verified on this device.
            </p>
            <p className="muted text-sm" style={{ margin: 0 }}>
              Now let them verify you too: they tap <strong>Improve safety</strong> in this chat →{" "}
              <strong>Show my code</strong>.
            </p>
          </>
        )}

        {outcome.kind === "mismatch" && (
          <div className="assurance" role="alert">
            <strong>The code does not match.</strong> Your devices hold different keys — do not
            trust this connection. Retry only if the screen was hard to read; a repeated mismatch
            can mean impersonation.
          </div>
        )}

        {outcome.kind === "notverify" && (
          <p className="scan-status" role="alert" style={{ margin: 0, color: "var(--danger, #c44)" }}>
            That QR is not a Veto verify code. Scan the code from their <strong>Show my code</strong>{" "}
            screen.
          </p>
        )}

        {outcome.kind === "error" && (
          <p className="scan-status" role="alert" style={{ margin: 0, color: "var(--danger, #c44)" }}>
            {outcome.message}
          </p>
        )}

        <div className="actions-row">
          {outcome.kind === "match" ? (
            <button className="btn" type="button" onClick={onClose}>
              Done
            </button>
          ) : (
            <>
              <button className="btn secondary" type="button" onClick={onClose}>
                Cancel
              </button>
              {outcome.kind !== "starting" && (
                <button
                  className="btn"
                  type="button"
                  onClick={() => {
                    setOutcome({ kind: "starting" });
                    setRound((r) => r + 1);
                  }}
                >
                  Scan again
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </ModalShell>
  );

  // Portal over the details sheet: unaffected by ancestor transforms,
  // backdrop-filters or overflow clipping (robust stacking everywhere).
  if (typeof document === "undefined") return modal;
  return createPortal(modal, document.body);
}
